import {
  Globe,
  ShieldCheck,
  Palette,
  MessageCircle,
  BarChart3,
  TrendingUp,
  Target,
  ShoppingCart,
  Settings,
  Store,
  Boxes,
  ShoppingBag,
  Search,
  FileText,
} from "lucide-react";

import type { ServicePage } from "@/types/service";

export const ECommerceSolutions: ServicePage[] = [
  // Opencart Development
  {
    title: "OpenCart Development",
    slug: "opencart-development",
    group: "E-Commerce Solutions",
    icon: ShoppingCart,

    seo: {
      title: "OpenCart Development Services",
      description:
        "Build scalable and conversion-focused online stores with professional OpenCart development services covering custom design, extensions, payment integrations, and performance optimization.",
      keywords: [
        "OpenCart Development",
        "OpenCart Development Services",
        "OpenCart Website Development",
        "OpenCart Store Development",
        "OpenCart Developers",
        "OpenCart Development Company",
      ],
    },

    hero: {
      title: "OpenCart Development for Powerful E-Commerce Experiences",
      description:
        "Build secure, flexible, and easy-to-manage online stores with custom OpenCart development tailored to your products, customers, business workflows, and growth objectives.",
      highlights: [
        "Custom OpenCart Stores",
        "Theme Development",
        "Module Development",
        "Payment Integration",
      ],
    },

    whyChoose: {
      title: "Why Choose OpenCart for E-Commerce?",
      description:
        "OpenCart provides businesses with a flexible e-commerce foundation for managing products, orders, customers, payments, and store operations from a centralized platform.",
      items: [
        {
          title: "Flexible Store Management",
          description:
            "Manage products, categories, customers, orders, promotions, and other store operations from an intuitive administration system.",
          icon: ShoppingCart,
        },
        {
          title: "Highly Customizable",
          description:
            "Extend your store with custom themes, modules, integrations, and functionality tailored to your business.",
          icon: Settings,
        },
        {
          title: "Multi-Store Capabilities",
          description:
            "Manage multiple storefronts and product catalogs through a centralized OpenCart installation.",
          icon: Store,
        },
        {
          title: "Scalable Commerce Foundation",
          description:
            "Build an e-commerce platform capable of evolving as your products, customers, and operational requirements grow.",
          icon: TrendingUp,
        },
      ],
    },

    services: {
      title: "Our OpenCart Development Services",
      description:
        "End-to-end OpenCart development solutions covering store creation, customization, integrations, migration, and ongoing optimization.",
      items: [
        {
          title: "Custom OpenCart Development",
          description:
            "Build customized OpenCart stores aligned with your business model, product catalog, and customer journey.",
          icon: ShoppingCart,
          features: [
            "Store Setup",
            "Custom Functionality",
            "Catalog Configuration",
            "Checkout Customization",
            "Admin Configuration",
          ],
        },
        {
          title: "OpenCart Theme Development",
          description:
            "Create responsive and conversion-focused storefront experiences tailored to your brand identity.",
          icon: Palette,
          features: [
            "Custom Theme Design",
            "Responsive Layouts",
            "Product Pages",
            "Category Pages",
            "Checkout UI",
          ],
        },
        {
          title: "OpenCart Module Development",
          description:
            "Extend your store with custom modules and functionality built around specific business requirements.",
          icon: Boxes,
          features: [
            "Custom Modules",
            "Extension Customization",
            "API Integrations",
            "Business Automation",
            "Third-Party Integration",
          ],
        },
        {
          title: "OpenCart Integration & Optimization",
          description:
            "Connect payment, shipping, analytics, and business systems while improving store performance and reliability.",
          icon: Settings,
          features: [
            "Payment Gateways",
            "Shipping Integration",
            "Analytics Integration",
            "Performance Optimization",
            "Store Maintenance",
          ],
        },
      ],
    },

    features: {
      title: "Key Features",
      description:
        "Essential capabilities for building flexible, manageable, and customer-friendly OpenCart stores.",
      items: [
        {
          title: "Product & Catalog Management",
          description:
            "Organize products, categories, attributes, options, pricing, inventory, and promotions efficiently.",
          icon: Boxes,
        },
        {
          title: "Secure Checkout",
          description:
            "Create streamlined checkout journeys with secure payment and order processing integrations.",
          icon: ShieldCheck,
        },
        {
          title: "Responsive Storefront",
          description:
            "Deliver consistent shopping experiences across desktop, tablet, and mobile devices.",
          icon: Globe,
        },
        {
          title: "Store Analytics",
          description:
            "Connect analytics and reporting tools to understand customer behaviour and commerce performance.",
          icon: BarChart3,
        },
      ],
    },

    process: {
      title: "Our OpenCart Development Process",
      description:
        "A structured development workflow focused on store usability, technical reliability, business operations, and scalable commerce.",
      items: [
        {
          title: "Business Requirement Analysis",
          description:
            "Understand your products, customers, business model, store workflows, integrations, and operational requirements.",
        },
        {
          title: "Store Architecture & Planning",
          description:
            "Define catalog structure, customer journeys, functionality, integrations, and technical implementation.",
        },
        {
          title: "UI & Theme Development",
          description:
            "Build responsive storefront experiences aligned with your brand and e-commerce objectives.",
        },
        {
          title: "OpenCart Development",
          description:
            "Configure the platform and develop required modules, features, checkout workflows, and integrations.",
        },
        {
          title: "Testing & Optimization",
          description:
            "Test products, payments, orders, responsive behaviour, functionality, performance, and critical store journeys.",
        },
        {
          title: "Launch & Support",
          description:
            "Deploy the store and provide ongoing technical support, maintenance, updates, and optimization as required.",
        },
      ],
    },

    benefits: {
      title: "Benefits of OpenCart Development",
      description:
        "Build a flexible e-commerce platform that simplifies store management while supporting customized customer experiences.",
      items: [
        {
          title: "Custom Shopping Experience",
          description:
            "Tailor storefront design and functionality around your products, customers, and business requirements.",
          icon: Palette,
        },
        {
          title: "Simplified Store Operations",
          description:
            "Manage products, customers, orders, promotions, and store settings through a centralized platform.",
          icon: Settings,
        },
        {
          title: "Multiple Payment Options",
          description:
            "Integrate appropriate payment gateways and checkout methods based on your market and customer requirements.",
          icon: ShieldCheck,
        },
        {
          title: "Extensible Functionality",
          description:
            "Add new modules, integrations, and capabilities as your e-commerce requirements evolve.",
          icon: Boxes,
        },
        {
          title: "Multi-Store Management",
          description:
            "Operate multiple storefronts from a centralized environment when your business model requires it.",
          icon: Store,
        },
        {
          title: "Growth-Ready Platform",
          description:
            "Create a commerce foundation that can evolve alongside your product catalog and operational needs.",
          icon: TrendingUp,
        },
      ],
    },

    techStack: {
      title: "OpenCart Technologies & Integrations",
      description:
        "Technologies and integrations used to build, customize, operate, and optimize OpenCart e-commerce platforms.",
      technologies: [
        "OpenCart",
        "PHP",
        "MySQL",
        "HTML5",
        "CSS3",
        "JavaScript",
        "REST APIs",
        "Payment Gateways",
      ],
    },

    whyUs: {
      title: "Why Choose Skand Tech Hub?",
      description:
        "We combine e-commerce development, UI engineering, integrations, and performance optimization to build OpenCart stores around practical business requirements.",
      items: [
        {
          title: "Custom Development",
          description:
            "Build store functionality around your business instead of forcing operations into generic workflows.",
          icon: Settings,
        },
        {
          title: "Conversion-Focused UI",
          description:
            "Create clear product discovery and checkout journeys designed around customer usability.",
          icon: Target,
        },
        {
          title: "Integration Expertise",
          description:
            "Connect payment gateways, shipping providers, APIs, analytics, and other business systems.",
          icon: Globe,
        },
        {
          title: "Performance Optimization",
          description:
            "Optimize storefront performance and technical implementation for smoother customer experiences.",
          icon: TrendingUp,
        },
        {
          title: "Secure Development",
          description:
            "Follow reliable development practices for store functionality, integrations, payments, and customer workflows.",
          icon: ShieldCheck,
        },
        {
          title: "Ongoing Technical Support",
          description:
            "Support your store with updates, troubleshooting, improvements, and additional development as requirements evolve.",
          icon: MessageCircle,
        },
      ],
    },

    industrySolutions: {
      title: "Industries We Serve",
      description:
        "OpenCart solutions tailored to different product catalogs, commerce workflows, customer journeys, and operational requirements.",
      items: [
        {
          title: "Fashion & Apparel",
          description:
            "Build product-rich stores with variants, categories, promotions, and streamlined shopping journeys.",
          icon: ShoppingCart,
        },
        {
          title: "Electronics",
          description:
            "Manage detailed product catalogs, specifications, inventory, pricing, and customer purchasing workflows.",
          icon: Boxes,
        },
        {
          title: "Beauty & Cosmetics",
          description:
            "Create visually engaging storefronts designed around product discovery, collections, and repeat purchases.",
          icon: Palette,
        },
        {
          title: "Home & Lifestyle",
          description:
            "Build organized commerce experiences for broad catalogs, categories, product options, and promotions.",
          icon: Store,
        },
        {
          title: "B2B Commerce",
          description:
            "Develop customized ordering workflows, catalogs, customer groups, and integrations for business buyers.",
          icon: Globe,
        },
        {
          title: "Multi-Store Retail",
          description:
            "Manage multiple storefronts, product catalogs, and customer segments through centralized commerce infrastructure.",
          icon: TrendingUp,
        },
      ],
    },

    portfolio: {
      title: "OpenCart Development Success Stories",
      description:
        "E-commerce projects focused on customized storefronts, efficient operations, integrations, and stronger shopping experiences.",
      items: [
        {
          title: "Multi-Category Retail Store",
          description:
            "Developed a structured OpenCart storefront with customized catalog navigation, product workflows, payments, and order management.",
          image: "/project-image.webp",
        },
        {
          title: "B2B Commerce Platform",
          description:
            "Built customized catalog and ordering functionality around business-specific purchasing requirements and customer workflows.",
          image: "/project-image.webp",
        },
        {
          title: "Store Performance Optimization",
          description:
            "Improved an existing OpenCart implementation through technical optimization, storefront refinements, and integration improvements.",
          image: "/project-image.webp",
        },
      ],
    },

    hire: {
      title: "Hire OpenCart Developers",
      description:
        "Partner with our OpenCart developers to build, customize, integrate, migrate, and optimize scalable e-commerce stores around your products and business workflows.",
      cta: "Let's Build Your OpenCart Store",
    },

    testimonials: {
      title: "What Our Clients Say",
      description:
        "Businesses trust Skand Tech Hub to build and customize OpenCart stores around their commerce requirements.",
      items: [
        {
          name: "Oliver Turner",
          company: "RetailCore",
          location: "United Kingdom",
          review:
            "The team understood our catalog and checkout requirements clearly and delivered a much more organized OpenCart store that is easier for both customers and our internal team to use.",
        },
        {
          name: "Emily Carter",
          company: "MarketLane",
          location: "Australia",
          review:
            "Skand Tech Hub handled the store customization and integrations smoothly. The new storefront is faster, cleaner, and much easier for us to manage.",
        },
      ],
    },

    faq: {
      title: "Frequently Asked Questions",
      description: "Common questions about our OpenCart Development services.",
      items: [
        {
          question: "What OpenCart development services do you provide?",
          answer:
            "We provide OpenCart store development, theme customization, module development, payment and shipping integrations, API integrations, migration, performance optimization, maintenance, and ongoing technical support.",
        },
        {
          question: "Can you customize an existing OpenCart store?",
          answer:
            "Yes. We can customize existing OpenCart stores, including themes, product and checkout workflows, modules, integrations, functionality, and performance.",
        },
        {
          question: "Can you develop custom OpenCart modules?",
          answer:
            "Yes. We can develop custom modules and integrations when standard OpenCart functionality or available extensions do not meet your business requirements.",
        },
        {
          question: "Can you integrate payment gateways with OpenCart?",
          answer:
            "Yes. We can integrate suitable payment gateways based on your market, provider requirements, checkout workflow, and technical environment.",
        },
        {
          question: "Do you provide OpenCart maintenance and support?",
          answer:
            "Yes. We can provide ongoing maintenance, troubleshooting, updates, performance improvements, integration support, and additional development for existing OpenCart stores.",
        },
      ],
    },
  },
  // Magento Development
  {
    title: "Magento Development",
    slug: "magento-development",
    group: "E-Commerce Solutions",
    icon: Store,

    seo: {
      title: "Magento Development Services",
      description:
        "Build scalable and feature-rich e-commerce platforms with professional Magento development services covering custom stores, extensions, integrations, migration, and performance optimization.",
      keywords: [
        "Magento Development",
        "Magento Development Services",
        "Magento Website Development",
        "Magento E-Commerce Development",
        "Magento Developers",
        "Magento Development Company",
      ],
    },

    hero: {
      title: "Magento Development for Scalable E-Commerce Growth",
      description:
        "Build powerful and flexible Magento stores designed for complex catalogs, customized shopping experiences, business integrations, and growing e-commerce operations.",
      highlights: [
        "Custom Magento Stores",
        "Theme Development",
        "Extension Development",
        "ERP & CRM Integration",
      ],
    },

    whyChoose: {
      title: "Why Choose Magento for E-Commerce?",
      description:
        "Magento provides a flexible commerce foundation for businesses that require advanced catalog management, customized workflows, integrations, and scalable e-commerce capabilities.",
      items: [
        {
          title: "Flexible Commerce Architecture",
          description:
            "Customize storefronts, catalogs, checkout workflows, pricing, promotions, and business processes around your requirements.",
          icon: Settings,
        },
        {
          title: "Advanced Catalog Management",
          description:
            "Manage large product catalogs, categories, attributes, pricing rules, inventory, and complex product configurations.",
          icon: Boxes,
        },
        {
          title: "Custom Integrations",
          description:
            "Connect your commerce platform with ERP, CRM, payment, shipping, analytics, and other business systems.",
          icon: Globe,
        },
        {
          title: "Scalable E-Commerce",
          description:
            "Build a commerce platform designed to support expanding catalogs, customers, markets, and operational requirements.",
          icon: TrendingUp,
        },
      ],
    },

    services: {
      title: "Our Magento Development Services",
      description:
        "End-to-end Magento development solutions covering custom storefronts, extensions, integrations, migration, optimization, and ongoing support.",
      items: [
        {
          title: "Custom Magento Development",
          description:
            "Build customized Magento commerce platforms aligned with your products, customer journeys, and business operations.",
          icon: Store,
          features: [
            "Custom Store Development",
            "Catalog Configuration",
            "Checkout Customization",
            "Business Workflows",
            "Admin Customization",
          ],
        },
        {
          title: "Magento Theme Development",
          description:
            "Create responsive and conversion-focused storefront experiences aligned with your brand identity.",
          icon: Palette,
          features: [
            "Custom Theme Design",
            "Responsive Storefronts",
            "Product Page Design",
            "Category Pages",
            "Checkout Experience",
          ],
        },
        {
          title: "Magento Extension Development",
          description:
            "Extend Magento functionality with custom extensions and business-specific commerce capabilities.",
          icon: Boxes,
          features: [
            "Custom Extensions",
            "Extension Customization",
            "API Development",
            "Business Automation",
            "Third-Party Integrations",
          ],
        },
        {
          title: "Magento Integration & Migration",
          description:
            "Connect Magento with critical business systems or migrate existing stores while preserving essential commerce data and workflows.",
          icon: Settings,
          features: [
            "ERP Integration",
            "CRM Integration",
            "Payment Integration",
            "Platform Migration",
            "Data Migration",
          ],
        },
      ],
    },

    features: {
      title: "Key Features",
      description:
        "Essential capabilities for building advanced, customizable, and scalable Magento commerce platforms.",
      items: [
        {
          title: "Advanced Product Management",
          description:
            "Manage complex catalogs, product types, attributes, categories, pricing, inventory, and promotional rules.",
          icon: Boxes,
        },
        {
          title: "Customized Checkout",
          description:
            "Create streamlined checkout experiences aligned with your payment, shipping, and customer requirements.",
          icon: ShoppingCart,
        },
        {
          title: "Multi-Store Commerce",
          description:
            "Manage multiple storefronts, brands, languages, currencies, and catalogs from centralized commerce infrastructure.",
          icon: Store,
        },
        {
          title: "Commerce Analytics",
          description:
            "Connect analytics and reporting systems to understand customer behaviour, conversions, and store performance.",
          icon: BarChart3,
        },
      ],
    },

    process: {
      title: "Our Magento Development Process",
      description:
        "A structured development workflow focused on commerce architecture, customer experience, integrations, performance, and long-term scalability.",
      items: [
        {
          title: "Business & Requirement Analysis",
          description:
            "Understand your products, customers, operational workflows, integrations, markets, and commerce objectives.",
        },
        {
          title: "Architecture & Store Planning",
          description:
            "Define catalog structure, storefront architecture, functionality, integrations, checkout flows, and technical requirements.",
        },
        {
          title: "UI & Theme Development",
          description:
            "Design and develop responsive storefront experiences aligned with your brand and customer journeys.",
        },
        {
          title: "Magento Development & Integration",
          description:
            "Develop custom functionality, extensions, commerce workflows, APIs, and required third-party integrations.",
        },
        {
          title: "Testing & Performance Optimization",
          description:
            "Test functionality, checkout, payments, integrations, security, responsive behaviour, and platform performance.",
        },
        {
          title: "Deployment & Support",
          description:
            "Launch the commerce platform and provide ongoing maintenance, monitoring, optimization, and development support.",
        },
      ],
    },

    benefits: {
      title: "Benefits of Magento Development",
      description:
        "Build a flexible commerce ecosystem capable of supporting sophisticated customer experiences and evolving business operations.",
      items: [
        {
          title: "Highly Customizable Commerce",
          description:
            "Adapt storefront functionality and business workflows around complex e-commerce requirements.",
          icon: Settings,
        },
        {
          title: "Advanced Catalog Control",
          description:
            "Manage extensive product catalogs, configurations, pricing structures, and promotional requirements.",
          icon: Boxes,
        },
        {
          title: "Scalable Store Architecture",
          description:
            "Create commerce infrastructure capable of supporting increasing products, customers, transactions, and markets.",
          icon: TrendingUp,
        },
        {
          title: "Integrated Business Systems",
          description:
            "Connect commerce operations with ERP, CRM, inventory, payment, shipping, and other enterprise systems.",
          icon: Globe,
        },
        {
          title: "Multi-Store Capabilities",
          description:
            "Operate multiple storefronts, brands, currencies, and markets through centralized commerce management.",
          icon: Store,
        },
        {
          title: "Secure Commerce Experience",
          description:
            "Build reliable customer, checkout, payment, and administrative workflows using secure development practices.",
          icon: ShieldCheck,
        },
      ],
    },

    techStack: {
      title: "Magento Technologies & Integrations",
      description:
        "Technologies used to develop, customize, integrate, and optimize modern Magento commerce platforms.",
      technologies: [
        "Magento",
        "Adobe Commerce",
        "PHP",
        "MySQL",
        "Elasticsearch",
        "REST APIs",
        "GraphQL",
        "Redis",
      ],
    },

    whyUs: {
      title: "Why Choose Skand Tech Hub?",
      description:
        "We combine commerce engineering, custom development, integrations, performance optimization, and customer experience thinking to build Magento solutions around real business requirements.",
      items: [
        {
          title: "Custom Commerce Development",
          description:
            "Develop Magento functionality around your workflows, products, customers, and operational requirements.",
          icon: Settings,
        },
        {
          title: "Conversion-Focused Storefronts",
          description:
            "Create intuitive product discovery, shopping, and checkout experiences designed around customer usability.",
          icon: Target,
        },
        {
          title: "Integration Expertise",
          description:
            "Connect Magento with ERP, CRM, payments, shipping, analytics, inventory, and other business systems.",
          icon: Globe,
        },
        {
          title: "Performance Optimization",
          description:
            "Improve technical implementation and storefront performance for smoother commerce experiences.",
          icon: TrendingUp,
        },
        {
          title: "Secure Development",
          description:
            "Follow reliable engineering practices across customer data, checkout, integrations, and administrative functionality.",
          icon: ShieldCheck,
        },
        {
          title: "Ongoing Technical Support",
          description:
            "Support your Magento platform with troubleshooting, updates, optimization, integrations, and additional development.",
          icon: MessageCircle,
        },
      ],
    },

    industrySolutions: {
      title: "Industries We Serve",
      description:
        "Magento commerce solutions tailored to businesses with complex catalogs, customer journeys, integrations, and operational requirements.",
      items: [
        {
          title: "Fashion & Apparel",
          description:
            "Build scalable stores with product variants, collections, promotions, inventory, and personalized shopping experiences.",
          icon: ShoppingCart,
        },
        {
          title: "Electronics",
          description:
            "Manage detailed product specifications, comparisons, large catalogs, inventory, and complex purchasing journeys.",
          icon: Boxes,
        },
        {
          title: "B2B Commerce",
          description:
            "Create customized catalogs, pricing, customer groups, ordering workflows, and integrations for business buyers.",
          icon: Globe,
        },
        {
          title: "Beauty & Cosmetics",
          description:
            "Develop visually engaging commerce experiences around collections, product discovery, promotions, and repeat purchases.",
          icon: Palette,
        },
        {
          title: "Home & Lifestyle",
          description:
            "Manage extensive catalogs, product options, categories, shipping requirements, and customer shopping journeys.",
          icon: Store,
        },
        {
          title: "Multi-Brand Retail",
          description:
            "Operate multiple brands, storefronts, catalogs, customer segments, and markets through centralized commerce infrastructure.",
          icon: TrendingUp,
        },
      ],
    },

    portfolio: {
      title: "Magento Development Success Stories",
      description:
        "Magento projects focused on scalable commerce architecture, customized customer experiences, integrations, and operational efficiency.",
      items: [
        {
          title: "Enterprise Retail Platform",
          description:
            "Developed a Magento commerce experience with complex catalog structures, customized checkout workflows, and business integrations.",
          image: "/project-image.webp",
        },
        {
          title: "B2B Commerce Solution",
          description:
            "Built custom customer groups, catalog workflows, ordering functionality, and integrations around B2B purchasing requirements.",
          image: "/project-image.webp",
        },
        {
          title: "Multi-Store Commerce Platform",
          description:
            "Structured multiple storefronts and catalogs within centralized Magento infrastructure for streamlined commerce management.",
          image: "/project-image.webp",
        },
      ],
    },

    hire: {
      title: "Hire Magento Developers",
      description:
        "Partner with our Magento developers to build, customize, integrate, migrate, and optimize scalable commerce platforms around complex business and customer requirements.",
      cta: "Let's Build Your Magento Store",
    },

    testimonials: {
      title: "What Our Clients Say",
      description:
        "Businesses trust Skand Tech Hub to build and customize Magento platforms around demanding commerce requirements.",
      items: [
        {
          name: "William Parker",
          company: "CommerceGrid",
          location: "United States",
          review:
            "Skand Tech Hub handled our complex catalog and integration requirements very well. The new Magento setup gives our team much better control over daily commerce operations.",
        },
        {
          name: "Sophie Mitchell",
          company: "RetailSphere",
          location: "United Kingdom",
          review:
            "The team understood both the technical and customer experience sides of the project. Our storefront is now easier to manage and much more aligned with our business workflows.",
        },
      ],
    },

    faq: {
      title: "Frequently Asked Questions",
      description: "Common questions about our Magento Development services.",
      items: [
        {
          question: "What Magento development services do you provide?",
          answer:
            "We provide custom Magento store development, theme development, extension development, API integrations, payment and shipping integrations, migration, performance optimization, maintenance, and ongoing technical support.",
        },
        {
          question: "Can you customize an existing Magento store?",
          answer:
            "Yes. We can customize existing Magento stores, including themes, extensions, catalog functionality, checkout workflows, integrations, administration features, and performance.",
        },
        {
          question: "Can you integrate Magento with ERP and CRM systems?",
          answer:
            "Yes. Depending on the available APIs and system requirements, Magento can be integrated with ERP, CRM, inventory, accounting, fulfillment, and other business platforms.",
        },
        {
          question: "Can you migrate an existing e-commerce store to Magento?",
          answer:
            "Yes. We can plan and implement migrations involving products, categories, customers, orders, content, URLs, and other relevant commerce data based on the source platform and project requirements.",
        },
        {
          question: "Do you provide Magento maintenance and support?",
          answer:
            "Yes. We can provide ongoing troubleshooting, updates, performance optimization, integration support, security-related maintenance, and additional Magento development.",
        },
      ],
    },
  },
  // WooCommerce Development
  {
    title: "WooCommerce Development",
    slug: "woocommerce-development",
    group: "E-Commerce Solutions",
    icon: ShoppingBag,

    seo: {
      title: "WooCommerce Development Services",
      description:
        "Build flexible and conversion-focused online stores with professional WooCommerce development services covering custom design, plugins, payment integrations, migration, and optimization.",
      keywords: [
        "WooCommerce Development",
        "WooCommerce Development Services",
        "WooCommerce Website Development",
        "WooCommerce Store Development",
        "WooCommerce Developers",
        "WooCommerce Development Company",
      ],
    },

    hero: {
      title: "WooCommerce Development for Flexible Online Selling",
      description:
        "Build secure, responsive, and easy-to-manage WooCommerce stores with customized shopping experiences, payment integrations, business functionality, and scalable e-commerce architecture.",
      highlights: [
        "Custom WooCommerce Stores",
        "Theme Development",
        "Plugin Development",
        "Payment Integration",
      ],
    },

    whyChoose: {
      title: "Why Choose WooCommerce for E-Commerce?",
      description:
        "WooCommerce combines WordPress flexibility with powerful e-commerce capabilities, making it ideal for businesses that need customizable storefronts and straightforward content management.",
      items: [
        {
          title: "Flexible Store Customization",
          description:
            "Customize storefront design, products, checkout, customer journeys, and commerce functionality around your business.",
          icon: Settings,
        },
        {
          title: "Easy Content Management",
          description:
            "Manage products, pages, blogs, promotions, and other website content through the familiar WordPress ecosystem.",
          icon: FileText,
        },
        {
          title: "Extensive Integration Options",
          description:
            "Connect payments, shipping, CRM, analytics, marketing, and other business tools through plugins and APIs.",
          icon: Globe,
        },
        {
          title: "Growth-Ready Commerce",
          description:
            "Expand your store with new products, functionality, integrations, and customer experiences as your business grows.",
          icon: TrendingUp,
        },
      ],
    },

    services: {
      title: "Our WooCommerce Development Services",
      description:
        "End-to-end WooCommerce solutions covering custom store development, themes, plugins, integrations, migration, and performance optimization.",
      items: [
        {
          title: "Custom WooCommerce Development",
          description:
            "Build customized WooCommerce stores aligned with your products, customer journeys, and operational requirements.",
          icon: ShoppingBag,
          features: [
            "Store Setup",
            "Product Configuration",
            "Custom Functionality",
            "Checkout Customization",
            "Order Management",
          ],
        },
        {
          title: "WooCommerce Theme Development",
          description:
            "Create responsive and brand-focused storefronts designed around product discovery and conversion.",
          icon: Palette,
          features: [
            "Custom Theme Design",
            "Responsive Layouts",
            "Product Pages",
            "Category Pages",
            "Checkout UI",
          ],
        },
        {
          title: "WooCommerce Plugin Development",
          description:
            "Extend store functionality with custom plugins and integrations designed around specific business requirements.",
          icon: Boxes,
          features: [
            "Custom Plugins",
            "Plugin Customization",
            "API Integrations",
            "Business Automation",
            "Third-Party Integration",
          ],
        },
        {
          title: "WooCommerce Integration & Optimization",
          description:
            "Connect essential commerce systems and improve the performance, reliability, and usability of your store.",
          icon: Settings,
          features: [
            "Payment Gateways",
            "Shipping Integration",
            "CRM Integration",
            "Performance Optimization",
            "Store Maintenance",
          ],
        },
      ],
    },

    features: {
      title: "Key Features",
      description:
        "Essential capabilities for creating flexible, manageable, and customer-friendly WooCommerce stores.",
      items: [
        {
          title: "Product Management",
          description:
            "Manage products, categories, variations, attributes, inventory, pricing, discounts, and promotions.",
          icon: Boxes,
        },
        {
          title: "Optimized Checkout",
          description:
            "Create streamlined checkout journeys with secure payment and shipping integrations.",
          icon: ShoppingCart,
        },
        {
          title: "Responsive Storefront",
          description:
            "Deliver consistent shopping experiences across mobile, tablet, and desktop devices.",
          icon: Globe,
        },
        {
          title: "Commerce Analytics",
          description:
            "Connect analytics and tracking tools to measure customer behaviour, conversions, and store performance.",
          icon: BarChart3,
        },
      ],
    },

    process: {
      title: "Our WooCommerce Development Process",
      description:
        "A structured development workflow focused on customer experience, store management, integrations, performance, and reliable commerce operations.",
      items: [
        {
          title: "Business & Requirement Analysis",
          description:
            "Understand your products, customers, business model, operational workflows, integrations, and commerce objectives.",
        },
        {
          title: "Store Architecture & Planning",
          description:
            "Define product structure, customer journeys, functionality, content architecture, checkout, and technical requirements.",
        },
        {
          title: "UI & Theme Development",
          description:
            "Design and develop responsive storefront experiences aligned with your brand and customer expectations.",
        },
        {
          title: "WooCommerce Development",
          description:
            "Configure WooCommerce and develop required functionality, plugins, checkout workflows, and integrations.",
        },
        {
          title: "Testing & Optimization",
          description:
            "Test products, payments, shipping, orders, responsive behaviour, integrations, functionality, and performance.",
        },
        {
          title: "Launch & Support",
          description:
            "Deploy the store and provide ongoing maintenance, troubleshooting, updates, and optimization as required.",
        },
      ],
    },

    benefits: {
      title: "Benefits of WooCommerce Development",
      description:
        "Create a customizable e-commerce platform that combines powerful selling capabilities with flexible website and content management.",
      items: [
        {
          title: "Highly Customizable",
          description:
            "Customize store design, product experiences, checkout workflows, and functionality around your business requirements.",
          icon: Settings,
        },
        {
          title: "Content & Commerce Together",
          description:
            "Manage website content and e-commerce operations within the same WordPress environment.",
          icon: FileText,
        },
        {
          title: "Flexible Payment Integration",
          description:
            "Integrate payment gateways suitable for your customers, market, currencies, and checkout requirements.",
          icon: ShieldCheck,
        },
        {
          title: "Extensible Functionality",
          description:
            "Add plugins, custom features, APIs, and integrations as your commerce requirements evolve.",
          icon: Boxes,
        },
        {
          title: "SEO-Friendly Foundation",
          description:
            "Combine WordPress content capabilities with structured product and category experiences for stronger search visibility.",
          icon: Search,
        },
        {
          title: "Scalable Store Growth",
          description:
            "Expand your product catalog, marketing capabilities, integrations, and customer experiences over time.",
          icon: TrendingUp,
        },
      ],
    },

    techStack: {
      title: "WooCommerce Technologies & Integrations",
      description:
        "Technologies and integrations used to develop, customize, operate, and optimize WooCommerce stores.",
      technologies: [
        "WooCommerce",
        "WordPress",
        "PHP",
        "MySQL",
        "JavaScript",
        "REST APIs",
        "Razorpay",
        "Stripe",
      ],
    },

    whyUs: {
      title: "Why Choose Skand Tech Hub?",
      description:
        "We combine WordPress development, e-commerce engineering, integrations, UI design, and performance optimization to build WooCommerce stores around real business requirements.",
      items: [
        {
          title: "Custom Store Development",
          description:
            "Build WooCommerce functionality around your products, workflows, and customer requirements.",
          icon: Settings,
        },
        {
          title: "Conversion-Focused UI",
          description:
            "Create clear product discovery and checkout experiences designed around customer usability.",
          icon: Target,
        },
        {
          title: "Plugin & API Expertise",
          description:
            "Extend WooCommerce with custom plugins, third-party systems, APIs, and business integrations.",
          icon: Boxes,
        },
        {
          title: "Performance Optimization",
          description:
            "Improve storefront speed and technical implementation for smoother customer experiences.",
          icon: TrendingUp,
        },
        {
          title: "Secure Commerce Development",
          description:
            "Follow reliable practices across payments, customer information, checkout, plugins, and administrative functionality.",
          icon: ShieldCheck,
        },
        {
          title: "Ongoing Technical Support",
          description:
            "Support your store with troubleshooting, updates, improvements, integrations, and additional development.",
          icon: MessageCircle,
        },
      ],
    },

    industrySolutions: {
      title: "Industries We Serve",
      description:
        "WooCommerce solutions tailored to different products, customer journeys, business models, and online selling requirements.",
      items: [
        {
          title: "Fashion & Apparel",
          description:
            "Build engaging stores with product variations, collections, promotions, inventory, and streamlined purchasing experiences.",
          icon: ShoppingBag,
        },
        {
          title: "Beauty & Cosmetics",
          description:
            "Create visually engaging product experiences focused on discovery, collections, promotions, and repeat purchases.",
          icon: Palette,
        },
        {
          title: "Food & Grocery",
          description:
            "Develop commerce workflows for product catalogs, delivery options, payments, orders, and customer communication.",
          icon: ShoppingCart,
        },
        {
          title: "Home & Lifestyle",
          description:
            "Build organized storefronts for broad catalogs, product options, categories, promotions, and shipping requirements.",
          icon: Store,
        },
        {
          title: "Digital Products",
          description:
            "Create stores for digital products, downloadable resources, memberships, and other online offerings.",
          icon: Globe,
        },
        {
          title: "B2B Commerce",
          description:
            "Develop customized catalogs, pricing, ordering workflows, and integrations for business customers.",
          icon: Boxes,
        },
      ],
    },

    portfolio: {
      title: "WooCommerce Development Success Stories",
      description:
        "WooCommerce projects focused on customized storefronts, improved customer journeys, integrations, and efficient store management.",
      items: [
        {
          title: "Fashion E-Commerce Store",
          description:
            "Built a responsive WooCommerce storefront with product variations, optimized category navigation, payment integration, and streamlined checkout.",
          image: "/project-image.webp",
        },
        {
          title: "B2B Ordering Platform",
          description:
            "Developed customized product, pricing, customer, and ordering workflows around business purchasing requirements.",
          image: "/project-image.webp",
        },
        {
          title: "WooCommerce Store Optimization",
          description:
            "Improved an existing store through storefront refinements, performance optimization, checkout improvements, and integration updates.",
          image: "/project-image.webp",
        },
      ],
    },

    hire: {
      title: "Hire WooCommerce Developers",
      description:
        "Partner with our WooCommerce developers to build, customize, integrate, migrate, and optimize flexible online stores around your products and business workflows.",
      cta: "Let's Build Your WooCommerce Store",
    },

    testimonials: {
      title: "What Our Clients Say",
      description:
        "Businesses trust Skand Tech Hub to build and customize WooCommerce stores around their e-commerce requirements.",
      items: [
        {
          name: "Henry Collins",
          company: "UrbanCart",
          location: "United Kingdom",
          review:
            "Skand Tech Hub transformed our existing WooCommerce setup into a much cleaner and easier shopping experience. Managing products and orders is also significantly simpler now.",
        },
        {
          name: "Ava Richardson",
          company: "BloomMarket",
          location: "Australia",
          review:
            "The team handled our design, payment integration, and custom functionality very smoothly. The store now feels much more professional and aligned with our brand.",
        },
      ],
    },

    faq: {
      title: "Frequently Asked Questions",
      description:
        "Common questions about our WooCommerce Development services.",
      items: [
        {
          question: "What WooCommerce development services do you provide?",
          answer:
            "We provide custom WooCommerce store development, theme development, plugin customization, custom plugins, payment and shipping integrations, API integrations, migration, optimization, maintenance, and technical support.",
        },
        {
          question: "Can you customize my existing WooCommerce store?",
          answer:
            "Yes. We can customize existing stores, including themes, product pages, checkout workflows, plugins, integrations, functionality, and performance.",
        },
        {
          question: "Can you develop custom WooCommerce plugins?",
          answer:
            "Yes. We can develop custom plugins when your required functionality cannot be achieved effectively through existing WooCommerce features or available extensions.",
        },
        {
          question: "Can you integrate Razorpay or Stripe with WooCommerce?",
          answer:
            "Yes. We can integrate suitable payment gateways such as Razorpay, Stripe, and other providers based on your market, account configuration, and checkout requirements.",
        },
        {
          question: "Do you provide WooCommerce maintenance and support?",
          answer:
            "Yes. We can provide ongoing updates, troubleshooting, performance optimization, plugin support, integration maintenance, and additional WooCommerce development.",
        },
      ],
    },
  },
  // PrestaShop Development
  {
    title: "Prestashop Development",
    slug: "prestashop-development",
    group: "E-Commerce Solutions",
    icon: Boxes,

    seo: {
      title: "PrestaShop Development Services",
      description:
        "Build flexible and scalable online stores with professional PrestaShop development services covering custom stores, themes, modules, integrations, migration, and optimization.",
      keywords: [
        "PrestaShop Development",
        "PrestaShop Development Services",
        "PrestaShop Website Development",
        "PrestaShop Store Development",
        "PrestaShop Developers",
        "PrestaShop Development Company",
      ],
    },

    hero: {
      title: "PrestaShop Development for Flexible E-Commerce Growth",
      description:
        "Build secure, customizable, and easy-to-manage PrestaShop stores with tailored storefront experiences, powerful catalog management, payment integrations, and scalable commerce functionality.",
      highlights: [
        "Custom PrestaShop Stores",
        "Theme Development",
        "Module Development",
        "Payment Integration",
      ],
    },

    whyChoose: {
      title: "Why Choose PrestaShop for E-Commerce?",
      description:
        "PrestaShop provides a flexible e-commerce platform for businesses that need customizable storefronts, strong catalog management, localized selling capabilities, and control over store operations.",
      items: [
        {
          title: "Flexible Store Customization",
          description:
            "Customize storefront design, product experiences, checkout workflows, and commerce functionality around your business.",
          icon: Settings,
        },
        {
          title: "Powerful Catalog Management",
          description:
            "Manage products, categories, combinations, pricing, inventory, promotions, and other catalog requirements efficiently.",
          icon: Boxes,
        },
        {
          title: "Localized Commerce",
          description:
            "Support multiple languages, currencies, tax configurations, and market-specific commerce requirements.",
          icon: Globe,
        },
        {
          title: "Scalable Store Foundation",
          description:
            "Expand your catalog, functionality, integrations, and customer experiences as your business grows.",
          icon: TrendingUp,
        },
      ],
    },

    services: {
      title: "Our PrestaShop Development Services",
      description:
        "End-to-end PrestaShop solutions covering custom store development, themes, modules, integrations, migration, and performance optimization.",
      items: [
        {
          title: "Custom PrestaShop Development",
          description:
            "Build customized PrestaShop stores aligned with your products, customers, workflows, and business requirements.",
          icon: Store,
          features: [
            "Store Setup",
            "Catalog Configuration",
            "Custom Functionality",
            "Checkout Customization",
            "Admin Configuration",
          ],
        },
        {
          title: "PrestaShop Theme Development",
          description:
            "Create responsive and brand-focused storefront experiences designed around product discovery and conversion.",
          icon: Palette,
          features: [
            "Custom Theme Design",
            "Responsive Layouts",
            "Product Pages",
            "Category Pages",
            "Checkout UI",
          ],
        },
        {
          title: "PrestaShop Module Development",
          description:
            "Extend store capabilities with custom modules and integrations built around specific business requirements.",
          icon: Boxes,
          features: [
            "Custom Modules",
            "Module Customization",
            "API Integrations",
            "Business Automation",
            "Third-Party Integration",
          ],
        },
        {
          title: "PrestaShop Integration & Migration",
          description:
            "Connect essential commerce systems or migrate existing stores while maintaining important product and customer data.",
          icon: Settings,
          features: [
            "Payment Integration",
            "Shipping Integration",
            "CRM Integration",
            "Store Migration",
            "Data Migration",
          ],
        },
      ],
    },

    features: {
      title: "Key Features",
      description:
        "Essential capabilities for building flexible, localized, and customer-friendly PrestaShop stores.",
      items: [
        {
          title: "Catalog Management",
          description:
            "Manage products, combinations, categories, attributes, pricing, inventory, discounts, and promotions.",
          icon: Boxes,
        },
        {
          title: "Secure Checkout",
          description:
            "Create streamlined purchasing journeys with suitable payment, shipping, and order processing integrations.",
          icon: ShoppingCart,
        },
        {
          title: "Multi-Language & Currency",
          description:
            "Support localized storefront experiences for customers across different languages and markets.",
          icon: Globe,
        },
        {
          title: "Commerce Analytics",
          description:
            "Connect analytics and reporting tools to measure customer behaviour, conversions, and store performance.",
          icon: BarChart3,
        },
      ],
    },

    process: {
      title: "Our PrestaShop Development Process",
      description:
        "A structured development workflow focused on customer experience, commerce functionality, integrations, localization, and reliable store operations.",
      items: [
        {
          title: "Business & Requirement Analysis",
          description:
            "Understand your products, customers, target markets, business workflows, integrations, and commerce objectives.",
        },
        {
          title: "Store Architecture & Planning",
          description:
            "Define catalog structure, customer journeys, functionality, localization, checkout, and technical requirements.",
        },
        {
          title: "UI & Theme Development",
          description:
            "Design and develop responsive storefront experiences aligned with your brand and customer expectations.",
        },
        {
          title: "PrestaShop Development",
          description:
            "Configure the platform and develop required modules, custom functionality, commerce workflows, and integrations.",
        },
        {
          title: "Testing & Optimization",
          description:
            "Test products, payments, shipping, orders, localization, responsive behaviour, functionality, and performance.",
        },
        {
          title: "Launch & Support",
          description:
            "Deploy the store and provide ongoing maintenance, troubleshooting, updates, and optimization as required.",
        },
      ],
    },

    benefits: {
      title: "Benefits of PrestaShop Development",
      description:
        "Create a customizable commerce platform with strong catalog control, localization capabilities, and flexible store functionality.",
      items: [
        {
          title: "Customizable Storefront",
          description:
            "Tailor store design, product experiences, checkout, and functionality around your commerce requirements.",
          icon: Palette,
        },
        {
          title: "Strong Catalog Control",
          description:
            "Manage complex products, combinations, categories, inventory, pricing, discounts, and promotional rules.",
          icon: Boxes,
        },
        {
          title: "Localized Selling",
          description:
            "Configure languages, currencies, taxes, and storefront experiences for different customer markets.",
          icon: Globe,
        },
        {
          title: "Flexible Integrations",
          description:
            "Connect payments, shipping, CRM, analytics, APIs, and other business systems based on your requirements.",
          icon: Settings,
        },
        {
          title: "Responsive Shopping Experience",
          description:
            "Provide consistent product discovery and purchasing journeys across mobile, tablet, and desktop devices.",
          icon: ShoppingCart,
        },
        {
          title: "Growth-Ready Commerce",
          description:
            "Expand products, functionality, markets, and integrations as your e-commerce operations evolve.",
          icon: TrendingUp,
        },
      ],
    },

    techStack: {
      title: "PrestaShop Technologies & Integrations",
      description:
        "Technologies and integrations used to develop, customize, operate, and optimize PrestaShop commerce platforms.",
      technologies: [
        "PrestaShop",
        "PHP",
        "MySQL",
        "Symfony",
        "JavaScript",
        "REST APIs",
        "Payment Gateways",
        "Shipping APIs",
      ],
    },

    whyUs: {
      title: "Why Choose Skand Tech Hub?",
      description:
        "We combine e-commerce engineering, custom development, integrations, UI design, and performance optimization to build PrestaShop stores around practical business requirements.",
      items: [
        {
          title: "Custom Commerce Development",
          description:
            "Build PrestaShop functionality around your products, customers, markets, and operational workflows.",
          icon: Settings,
        },
        {
          title: "Conversion-Focused UI",
          description:
            "Create clear product discovery, cart, and checkout experiences designed around customer usability.",
          icon: Target,
        },
        {
          title: "Module & API Expertise",
          description:
            "Extend your store with custom modules, APIs, payment systems, shipping services, and third-party integrations.",
          icon: Boxes,
        },
        {
          title: "Localization Support",
          description:
            "Configure commerce experiences for multiple languages, currencies, taxes, and customer markets.",
          icon: Globe,
        },
        {
          title: "Performance Optimization",
          description:
            "Improve storefront performance and technical implementation for smoother customer experiences.",
          icon: TrendingUp,
        },
        {
          title: "Ongoing Technical Support",
          description:
            "Support your store with troubleshooting, updates, improvements, integrations, and additional development.",
          icon: MessageCircle,
        },
      ],
    },

    industrySolutions: {
      title: "Industries We Serve",
      description:
        "PrestaShop solutions tailored to different product catalogs, customer journeys, markets, and commerce requirements.",
      items: [
        {
          title: "Fashion & Apparel",
          description:
            "Build engaging stores with product combinations, collections, inventory, promotions, and streamlined shopping journeys.",
          icon: ShoppingCart,
        },
        {
          title: "Beauty & Cosmetics",
          description:
            "Create visually engaging storefronts focused on product discovery, collections, promotions, and repeat purchases.",
          icon: Palette,
        },
        {
          title: "Electronics",
          description:
            "Manage detailed product catalogs, specifications, combinations, pricing, inventory, and purchasing workflows.",
          icon: Boxes,
        },
        {
          title: "Home & Lifestyle",
          description:
            "Build structured commerce experiences for broad catalogs, product options, categories, and shipping requirements.",
          icon: Store,
        },
        {
          title: "International Retail",
          description:
            "Create localized storefronts supporting multiple languages, currencies, taxes, and regional customer experiences.",
          icon: Globe,
        },
        {
          title: "B2B Commerce",
          description:
            "Develop customized catalogs, customer workflows, pricing structures, and integrations for business buyers.",
          icon: TrendingUp,
        },
      ],
    },

    portfolio: {
      title: "PrestaShop Development Success Stories",
      description:
        "PrestaShop projects focused on customized storefronts, localized commerce, integrations, and efficient store management.",
      items: [
        {
          title: "Multi-Language Retail Store",
          description:
            "Built a localized PrestaShop storefront supporting multiple languages, currencies, product categories, payments, and customer journeys.",
          image: "/project-image.webp",
        },
        {
          title: "Fashion Commerce Platform",
          description:
            "Developed a responsive storefront with product combinations, collection navigation, promotional functionality, and streamlined checkout.",
          image: "/project-image.webp",
        },
        {
          title: "PrestaShop Store Optimization",
          description:
            "Improved an existing store through technical optimization, storefront refinements, module updates, and checkout improvements.",
          image: "/project-image.webp",
        },
      ],
    },

    hire: {
      title: "Hire PrestaShop Developers",
      description:
        "Partner with our PrestaShop developers to build, customize, integrate, migrate, and optimize flexible e-commerce stores around your products, markets, and business workflows.",
      cta: "Let's Build Your PrestaShop Store",
    },

    testimonials: {
      title: "What Our Clients Say",
      description:
        "Businesses trust Skand Tech Hub to build and customize PrestaShop stores around their commerce and localization requirements.",
      items: [
        {
          name: "Daniel Foster",
          company: "EuroMarket",
          location: "France",
          review:
            "Skand Tech Hub helped us restructure our PrestaShop store for multiple markets. The catalog, localization, and checkout experience are now much easier to manage.",
        },
        {
          name: "Isabella Moore",
          company: "StylePort",
          location: "United Kingdom",
          review:
            "The team handled our theme customization and store improvements very professionally. The new storefront feels cleaner, faster, and much more consistent with our brand.",
        },
      ],
    },

    faq: {
      title: "Frequently Asked Questions",
      description:
        "Common questions about our PrestaShop Development services.",
      items: [
        {
          question: "What PrestaShop development services do you provide?",
          answer:
            "We provide custom PrestaShop development, theme development, module development, payment and shipping integrations, API integrations, migration, performance optimization, maintenance, and ongoing technical support.",
        },
        {
          question: "Can you customize an existing PrestaShop store?",
          answer:
            "Yes. We can customize existing stores, including themes, product pages, catalog functionality, checkout workflows, modules, integrations, and performance.",
        },
        {
          question: "Can you develop custom PrestaShop modules?",
          answer:
            "Yes. We can develop custom modules when standard PrestaShop functionality or existing modules do not fully support your business requirements.",
        },
        {
          question: "Can PrestaShop support multiple languages and currencies?",
          answer:
            "Yes. PrestaShop supports multi-language and multi-currency commerce, and we can configure the store according to your target markets and localization requirements.",
        },
        {
          question: "Do you provide PrestaShop maintenance and support?",
          answer:
            "Yes. We can provide ongoing troubleshooting, updates, module support, performance optimization, integration maintenance, and additional PrestaShop development.",
        },
      ],
    },
  },
  // Shopify Development
  {
    title: "Shopify Development",
    slug: "shopify-development",
    group: "E-Commerce Solutions",
    icon: BarChart3,

    seo: {
      title: "Shopify Development Services",
      description:
        "Build high-performing and conversion-focused online stores with professional Shopify development services covering custom themes, integrations, migrations, and store optimization.",
      keywords: [
        "Shopify Development",
        "Shopify Development Services",
        "Shopify Website Development",
        "Shopify Store Development",
        "Shopify Developers",
        "Shopify Development Company",
      ],
    },

    hero: {
      title: "Shopify Development for High-Performing Online Stores",
      description:
        "Build fast, responsive, and conversion-focused Shopify stores with custom storefront experiences, seamless integrations, optimized checkout journeys, and scalable e-commerce functionality.",
      highlights: [
        "Custom Shopify Stores",
        "Theme Development",
        "App Integration",
        "Conversion Optimization",
      ],
    },

    whyChoose: {
      title: "Why Choose Shopify for E-Commerce?",
      description:
        "Shopify provides a powerful commerce platform that simplifies store management while giving businesses the flexibility to create professional and scalable shopping experiences.",
      items: [
        {
          title: "Easy Store Management",
          description:
            "Manage products, inventory, customers, orders, promotions, and content through a centralized commerce platform.",
          icon: Store,
        },
        {
          title: "Flexible Storefronts",
          description:
            "Customize themes, product experiences, navigation, content, and customer journeys around your brand.",
          icon: Palette,
        },
        {
          title: "Commerce Integrations",
          description:
            "Connect your store with marketing, payments, shipping, analytics, CRM, and other business tools.",
          icon: Globe,
        },
        {
          title: "Scalable E-Commerce",
          description:
            "Expand products, markets, integrations, and commerce functionality as your online business grows.",
          icon: TrendingUp,
        },
      ],
    },

    services: {
      title: "Our Shopify Development Services",
      description:
        "End-to-end Shopify development solutions covering custom stores, themes, integrations, migration, optimization, and ongoing support.",
      items: [
        {
          title: "Custom Shopify Development",
          description:
            "Build customized Shopify stores aligned with your products, brand identity, customer journeys, and business requirements.",
          icon: Store,
          features: [
            "Shopify Store Setup",
            "Product Configuration",
            "Custom Sections",
            "Store Functionality",
            "Checkout Configuration",
          ],
        },
        {
          title: "Shopify Theme Development",
          description:
            "Create responsive and conversion-focused Shopify storefronts designed around your brand and customer experience.",
          icon: Palette,
          features: [
            "Custom Shopify Themes",
            "Liquid Development",
            "Responsive Design",
            "Product Pages",
            "Collection Pages",
          ],
        },
        {
          title: "Shopify App & API Integration",
          description:
            "Connect Shopify with applications, APIs, marketing platforms, payment systems, and business tools.",
          icon: Boxes,
          features: [
            "Shopify App Integration",
            "Third-Party APIs",
            "CRM Integration",
            "Marketing Integration",
            "Business Automation",
          ],
        },
        {
          title: "Shopify Migration & Optimization",
          description:
            "Migrate existing stores to Shopify or improve current stores through performance, UX, and conversion optimization.",
          icon: Settings,
          features: [
            "Store Migration",
            "Product Migration",
            "Performance Optimization",
            "Conversion Optimization",
            "Store Maintenance",
          ],
        },
      ],
    },

    features: {
      title: "Key Features",
      description:
        "Essential capabilities for creating professional, manageable, and conversion-focused Shopify stores.",
      items: [
        {
          title: "Product & Collection Management",
          description:
            "Organize products, variants, collections, pricing, inventory, discounts, and merchandising efficiently.",
          icon: Boxes,
        },
        {
          title: "Optimized Shopping Experience",
          description:
            "Create intuitive product discovery, cart, and purchasing journeys designed around customer usability.",
          icon: ShoppingCart,
        },
        {
          title: "Responsive Storefront",
          description:
            "Deliver consistent shopping experiences across mobile, tablet, and desktop devices.",
          icon: Globe,
        },
        {
          title: "Commerce Analytics",
          description:
            "Connect analytics and tracking tools to understand customer behaviour, conversions, and store performance.",
          icon: BarChart3,
        },
      ],
    },

    process: {
      title: "Our Shopify Development Process",
      description:
        "A structured development workflow focused on customer experience, store functionality, integrations, performance, and scalable commerce.",
      items: [
        {
          title: "Business & Requirement Analysis",
          description:
            "Understand your products, customers, brand, business model, integrations, and e-commerce objectives.",
        },
        {
          title: "Store Architecture & Planning",
          description:
            "Define collections, navigation, customer journeys, functionality, integrations, and technical requirements.",
        },
        {
          title: "UI & Theme Development",
          description:
            "Design and develop responsive storefront experiences aligned with your brand and conversion objectives.",
        },
        {
          title: "Shopify Development & Integration",
          description:
            "Configure Shopify and implement required theme functionality, applications, APIs, and business integrations.",
        },
        {
          title: "Testing & Optimization",
          description:
            "Test products, navigation, cart, payments, responsive behaviour, integrations, functionality, and performance.",
        },
        {
          title: "Launch & Support",
          description:
            "Launch the store and provide ongoing technical support, improvements, maintenance, and optimization as required.",
        },
      ],
    },

    benefits: {
      title: "Benefits of Shopify Development",
      description:
        "Create a professional commerce experience that simplifies store operations while supporting customer acquisition and business growth.",
      items: [
        {
          title: "Faster Store Launch",
          description:
            "Build and launch professional e-commerce experiences on established commerce infrastructure.",
          icon: TrendingUp,
        },
        {
          title: "Easy Store Management",
          description:
            "Manage products, customers, orders, promotions, inventory, and content through a centralized interface.",
          icon: Settings,
        },
        {
          title: "Conversion-Focused Experience",
          description:
            "Create clear product discovery and purchasing journeys designed around customer usability and conversions.",
          icon: Target,
        },
        {
          title: "Extensive App Ecosystem",
          description:
            "Extend store capabilities through Shopify applications and integrations based on your business requirements.",
          icon: Boxes,
        },
        {
          title: "Multi-Channel Commerce",
          description:
            "Connect products and commerce workflows with relevant sales, marketing, and customer engagement channels.",
          icon: Globe,
        },
        {
          title: "Scalable Commerce Foundation",
          description:
            "Expand your product catalog, markets, functionality, and integrations as your online business grows.",
          icon: BarChart3,
        },
      ],
    },

    techStack: {
      title: "Shopify Technologies & Integrations",
      description:
        "Technologies and integrations used to build, customize, connect, and optimize modern Shopify stores.",
      technologies: [
        "Shopify",
        "Liquid",
        "Hydrogen",
        "JavaScript",
        "React",
        "Shopify APIs",
        "Klaviyo",
        "Google Analytics",
      ],
    },

    whyUs: {
      title: "Why Choose Skand Tech Hub?",
      description:
        "We combine Shopify development, UI engineering, e-commerce strategy, integrations, and conversion optimization to create stores built around practical business goals.",
      items: [
        {
          title: "Custom Shopify Development",
          description:
            "Build storefront experiences and functionality around your products, brand, customers, and business requirements.",
          icon: Settings,
        },
        {
          title: "Conversion-Focused Design",
          description:
            "Create clear product discovery, navigation, cart, and purchasing journeys designed around customer usability.",
          icon: Target,
        },
        {
          title: "Shopify Integration Expertise",
          description:
            "Connect applications, APIs, marketing tools, analytics, CRM systems, and other commerce technologies.",
          icon: Boxes,
        },
        {
          title: "Performance Optimization",
          description:
            "Improve storefront implementation and performance for smoother shopping experiences.",
          icon: TrendingUp,
        },
        {
          title: "Growth-Focused Commerce",
          description:
            "Build stores with marketing, customer retention, merchandising, and future expansion in mind.",
          icon: BarChart3,
        },
        {
          title: "Ongoing Technical Support",
          description:
            "Support your Shopify store with troubleshooting, updates, improvements, integrations, and additional development.",
          icon: MessageCircle,
        },
      ],
    },

    industrySolutions: {
      title: "Industries We Serve",
      description:
        "Shopify solutions tailored to different products, customer journeys, commerce models, and online selling requirements.",
      items: [
        {
          title: "Fashion & Apparel",
          description:
            "Create engaging storefronts with variants, collections, merchandising, promotions, and streamlined shopping journeys.",
          icon: ShoppingBag,
        },
        {
          title: "Beauty & Cosmetics",
          description:
            "Build visually engaging product experiences designed around collections, discovery, promotions, and repeat purchases.",
          icon: Palette,
        },
        {
          title: "Food & Beverage",
          description:
            "Develop organized storefronts for product catalogs, bundles, promotions, orders, and customer engagement.",
          icon: ShoppingCart,
        },
        {
          title: "Home & Lifestyle",
          description:
            "Create structured shopping experiences for broad catalogs, collections, product options, and shipping requirements.",
          icon: Store,
        },
        {
          title: "Health & Wellness",
          description:
            "Build professional commerce experiences around product education, subscriptions, customer journeys, and repeat purchases.",
          icon: ShieldCheck,
        },
        {
          title: "D2C Brands",
          description:
            "Develop brand-focused commerce experiences designed for direct customer acquisition, conversion, and retention.",
          icon: TrendingUp,
        },
      ],
    },

    portfolio: {
      title: "Shopify Development Success Stories",
      description:
        "Shopify projects focused on stronger storefront experiences, streamlined commerce operations, integrations, and conversion optimization.",
      items: [
        {
          title: "D2C Fashion Store",
          description:
            "Built a custom Shopify storefront with collection-based navigation, product variants, responsive layouts, and an optimized purchasing journey.",
          image: "/project-image.webp",
        },
        {
          title: "Beauty & Lifestyle Store",
          description:
            "Developed a branded Shopify experience with custom sections, product storytelling, marketing integrations, and customer retention workflows.",
          image: "/project-image.webp",
        },
        {
          title: "Shopify Store Optimization",
          description:
            "Improved an existing Shopify store through theme refinements, performance optimization, product page improvements, and conversion-focused UX updates.",
          image: "/project-image.webp",
        },
      ],
    },

    hire: {
      title: "Hire Shopify Developers",
      description:
        "Partner with our Shopify developers to build, customize, integrate, migrate, and optimize high-performing online stores around your products, customers, and growth objectives.",
      cta: "Let's Build Your Shopify Store",
    },

    testimonials: {
      title: "What Our Clients Say",
      description:
        "Businesses trust Skand Tech Hub to create Shopify stores focused on strong customer experiences and efficient commerce operations.",
      items: [
        {
          name: "James Wilson",
          company: "NorthLane",
          location: "United States",
          review:
            "Skand Tech Hub gave our Shopify store a much more professional structure. Product discovery is clearer, the mobile experience is better, and managing the storefront is easier for our team.",
        },
        {
          name: "Emma Harrison",
          company: "Bloom & Co.",
          location: "United Kingdom",
          review:
            "The team understood our brand and translated it into a clean Shopify experience. They handled the theme work and integrations smoothly from start to finish.",
        },
      ],
    },

    faq: {
      title: "Frequently Asked Questions",
      description: "Common questions about our Shopify Development services.",
      items: [
        {
          question: "What Shopify development services do you provide?",
          answer:
            "We provide Shopify store development, custom theme development, theme customization, app and API integrations, store migration, performance optimization, conversion improvements, maintenance, and ongoing technical support.",
        },
        {
          question: "Can you customize an existing Shopify store?",
          answer:
            "Yes. We can customize existing Shopify stores, including themes, sections, product pages, collections, navigation, applications, integrations, and storefront functionality.",
        },
        {
          question: "Can you migrate my existing store to Shopify?",
          answer:
            "Yes. We can plan migrations involving products, collections, customers, content, URLs, and other relevant store data depending on your existing platform and project requirements.",
        },
        {
          question: "Do you integrate third-party apps with Shopify?",
          answer:
            "Yes. We can integrate appropriate Shopify applications, marketing tools, CRM systems, analytics platforms, APIs, payment solutions, and other third-party services.",
        },
        {
          question: "Do you provide Shopify maintenance and support?",
          answer:
            "Yes. We can provide ongoing troubleshooting, theme updates, integration support, storefront improvements, performance optimization, and additional Shopify development.",
        },
      ],
    },
  },
];
